// /src/pages/ToolsPage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ExternalLink, ArrowRight, Check } from 'lucide-react';
import { cardCls, headingCls, bodyText, mutedText, ctaPrimary, ctaSecondary, pageHeading, sectionLabel, pageWrap, sectionInner } from '../components/styles';
import { Chip } from '../components/Chip';
import { tools } from '../data/tools';

export default function ToolsPage() {
  return (
    <div className={pageWrap}>
      {/* Hero */}
      <section className="py-12" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl animate-stagger">
            <h1 className={`${pageHeading} mb-4`}><span className="opacity-50">$</span> platforms</h1>
            <p className={`text-xl ${mutedText} max-w-3xl`}>
              The flagship systems I've built. HADES handles metadata forensics. VitalChain handles healthcare
              blockchain. Both are production-grade with full documentation.
            </p>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Tool cards */}
      <section className="py-10" style={{ background: 'var(--bg2)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl space-y-10 animate-stagger">
            {tools.map((tool) => (
              <div key={tool.slug} className={`glow-card ${cardCls} p-8 border-l-4`} style={{ borderLeftColor: 'var(--accent)' }}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <Link to={`/tools/${tool.slug}`} className="hover:opacity-80 transition">
                      <h2 className={`text-3xl ${headingCls} mb-2`}>{tool.name}</h2>
                    </Link>
                    <p className={`text-lg ${mutedText}`}>{tool.tagline}</p>
                  </div>
                  <span className="font-mono text-xs font-semibold px-4 py-2 rounded-lg flex-shrink-0 border border-[color:var(--border)] bg-[var(--bg2)]"
                    style={{
                      color:
                        tool.status === 'Production' ? '#a7df6a'
                          : tool.status === 'Pilot' ? '#0abfda'
                          : 'var(--muted)'
                    }}
                  >
                    {tool.status.toUpperCase()}
                  </span>
                </div>

                {/* Metrics row */}
                {tool.metrics && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    {tool.metrics.map((m, i) => (
                      <div key={i} className="rounded-lg p-3 border border-[color:var(--border)] bg-[var(--bg2)]">
                        <div className="metric-value text-xl">{m.value}</div>
                        <div className={`text-xs ${mutedText} mt-1`}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                <p className={`${bodyText} text-lg mb-6 leading-relaxed`}>{tool.description}</p>

                {/* Tech stack chips */}
                {tool.techStack && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {tool.techStack.slice(0, 8).map((tech) => (
                      <Chip key={tech}>{tech}</Chip>
                    ))}
                    {tool.techStack.length > 8 && (
                      <span className={`text-sm ${mutedText} self-center`}>+{tool.techStack.length - 8} more</span>
                    )}
                  </div>
                )}

                <div className="rounded-lg p-6 mb-6 border border-[color:var(--border)] bg-[var(--bg2)]">
                  <h4 className={`font-bold ${headingCls} mb-3`}>Key Features:</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {tool.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Check className="text-[var(--accent-2)]" size={20} />
                        <span className={bodyText}>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Link to={`/tools/${tool.slug}`} className={ctaPrimary}>
                    <ArrowRight size={20} /> Full Deep-Dive
                  </Link>
                  {tool.docs && (
                    <a href={tool.docsUrl} target="_blank" rel="noopener noreferrer" className={ctaSecondary}>
                      <BookOpen size={20} /> Documentation
                    </a>
                  )}
                  {tool.demo && (
                    <a href={tool.demoUrl} target="_blank" rel="noopener noreferrer" className={ctaSecondary}>
                      <ExternalLink size={20} /> Try Demo
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Roadmap */}
      <section className="py-10" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <h2 className={`${sectionLabel} mb-6`}>{'// roadmap'}</h2>
            <div
              className="rounded-lg p-8 border border-[color:var(--border)]"
              style={{ background: 'linear-gradient(135deg, rgba(10,191,218,0.08), rgba(193,232,111,0.08))' }}
            >
              <ul className={`space-y-3 ${bodyText}`}>
                <li className="flex items-start gap-2">
                  <ArrowRight className="text-[var(--accent)] mt-1" size={20} />
                  <span><strong>VitalChain Health:</strong> Moving from AWS pilot to production; pre-production compliance checklist for real patient data with Vermont EMS</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="text-[var(--accent)] mt-1" size={20} />
                  <span><strong>HADES v1.x:</strong> Expanding firmware threat detection and refining the ML ensemble scoring pipeline post-v1.0.0 release</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="text-[var(--accent)] mt-1" size={20} />
                  <span><strong>Open Source:</strong> Preparing more internal tools for public release; see the open-source page for what has shipped so far</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
